import clsx from "clsx";
import { tab } from "@/models";
import { RedirectToOUI } from "@/components/RedirectToOUI";
import { useState } from "react";
import { Icon } from "@/components/Icons";
import { useNavigate } from "react-router-dom";
import { useCheckSubscription } from "@/providers/auth/hooks";

interface Props {
  data: tab[];
  activeTab: string;
  setActiveTab: (name: string | number) => void;
  wrapClassName?: string;
  className?: string;
}

export const TabUnderline = ({
  data,
  activeTab,
  setActiveTab,
  wrapClassName,
  className,
}: Props) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const checkSubscription = useCheckSubscription();

  const handleMouseEnter = (tabName: string) => {
    setOpenDropdown(tabName);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const handleNavigate = (to: string, type: string) => {
    if (type === "NewUI") {
      const currentPath = window.location.hash;
      navigate(to, { state: { navigationSource: currentPath } });
    }
  };

  return (
    <div className={clsx("border-b border-neutral-20", wrapClassName)}>
      <nav className="-mb-px flex" aria-label="Tabs">
        {data.map((item) => {
          const isActive = item.name === activeTab || item.id === activeTab;
          const hasChildren = item.children && item.children.length > 0;
          const isDropdownOpen = openDropdown === item.name;

          return (
            checkSubscription(item.subscriptions) && (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                {item.navigation && !hasChildren ? (
                  <>
                    {item.navigation.type === "OldUI" && (
                      <RedirectToOUI to={item.navigation.to}>
                        <span
                          className={clsx(
                            isActive
                              ? "border-blue text-blue"
                              : "border-transparent text-neutral-50 hover:text-blue hover:border-blue",
                            "cursor-pointer whitespace-nowrap px-3 border-b-2 font-medium text-sm inline-flex items-center h-[3.125rem]"
                          )}
                        >
                          {item.name}
                          {typeof item.number === "number" && (
                            <span className="h-6 w-6 rounded-full bg-neutral-20 ml-2 flex items-center justify-center font-medium text-neutral-80">
                              {item.number}
                            </span>
                          )}
                        </span>
                      </RedirectToOUI>
                    )}
                    {item.navigation.type === "NewUI" && (
                      <span
                        onClick={() =>
                          item.navigation &&
                          handleNavigate(
                            item.navigation.to,
                            item.navigation.type
                          )
                        }
                        className={clsx(
                          isActive
                            ? "border-blue text-blue"
                            : "border-transparent text-neutral-50 hover:text-blue hover:border-blue",
                          "cursor-pointer whitespace-nowrap px-3 border-b-2 font-medium text-sm inline-flex items-center h-[3.125rem]"
                        )}
                      >
                        {item.name}
                        {typeof item.number === "number" && (
                          <span className="h-6 w-6 rounded-full bg-neutral-20 ml-2 flex items-center justify-center font-medium text-neutral-80">
                            {item.number}
                          </span>
                        )}
                      </span>
                    )}
                  </>
                ) : (
                  <span
                    onClick={() => {
                      if (!hasChildren) {
                        setActiveTab(item.id || item.name);
                      }
                    }}
                    className={clsx(
                      isActive
                        ? "border-blue text-blue"
                        : "border-transparent text-neutral-50 hover:text-blue hover:border-blue",
                      "cursor-pointer whitespace-nowrap px-3 border-b-2 font-medium text-sm inline-flex items-center h-[3.125rem]",
                      className
                    )}
                  >
                    {item.name}
                    {typeof item.number === "number" && (
                      <span className="h-6 w-6 rounded-full bg-neutral-20 ml-2 flex items-center justify-center font-medium text-neutral-80">
                        {item.number}
                      </span>
                    )}
                    {hasChildren && (
                      <Icon.AngleDown
                        className="ml-1.5 stroke-neutral-50"
                        size="10"
                      />
                    )}
                  </span>
                )}

                {/* Dropdown menu for tabs with children */}
                {hasChildren && isDropdownOpen && item.children && (
                  <div className="absolute top-full left-0 mt-0 z-10 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {item.children.map(
                        (child) =>
                          checkSubscription(child.subscriptions) && (
                            <div key={child.name}>
                              {child.navigation &&
                                child.navigation.type === "OldUI" && (
                                  <RedirectToOUI
                                    to={child.navigation.to}
                                    key={child.name}
                                  >
                                    <span className="block px-4 py-2 text-sm text-neutral-80 hover:bg-neutral-10 cursor-pointer">
                                      {child.name}
                                    </span>
                                  </RedirectToOUI>
                                )}
                              {child.navigation &&
                                child.navigation.type === "NewUI" && (
                                  <span
                                    onClick={() =>
                                      child.navigation &&
                                      handleNavigate(
                                        child.navigation.to,
                                        child.navigation.type
                                      )
                                    }
                                    className="block px-4 py-2 text-sm text-neutral-80 hover:bg-neutral-10 cursor-pointer"
                                  >
                                    {child.name}
                                  </span>
                                )}
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          );
        })}
      </nav>
    </div>
  );
};
